import { AnalyticsService } from "../core/analytics.service";
import { AppointmentBookingState } from "./appointment-booking-state";
import { AppointmentInventory } from "./provider-inventories";
import { AppointmentSearchState } from "./appointment-search-state";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { TypeaheadSuggestion } from "@app/shared/typeahead/typeahead-suggestion";
import { SurveyQuestion } from "../survey/survey-models";

export enum TrackableFilterType {
  DATE = "date",
  LOCATION = "location",
}

export enum AppointmentAnalyticsProperty {
  BookingFlow = "Appointment Booking",
  BookingPageModule = "Appointment Booking Page",
  InventoryPageModule = "Apponitment Inventory Page",
}

@Injectable()
export class AppointmentAnalyticsService extends AnalyticsService {
  protected bookingState: AppointmentBookingState;
  protected searchState: AppointmentSearchState;

  protected track(eventName: string, properties?: { [index: string]: any }): Observable<any> {
    const trackProps = {
      ...properties,
      ...(this.bookingState && this.bookingState.trackableProperties),
      ...(this.searchState && this.searchState.trackableProperties),
    };
    return super.track(eventName, trackProps);
  }

  setBookingState(bookingState: AppointmentBookingState) {
    this.bookingState = bookingState;
  }

  setSearchState(searchState: AppointmentSearchState) {
    this.searchState = searchState;
  }

  bookingStarted() {
    return this.track("Appointment Booking Started");
  }

  reasonRemoved(selectedReason: object) {
    return this.track("Appointment Reason Deleted", { deleted_reason: selectedReason });
  }

  reasonSubmitted() {
    return this.track("Appointment Reason Submitted");
  }

  appointmentReasonTypeaheadSelected(suggestion: TypeaheadSuggestion) {
    return this.track("Appointment Reason Typeahead Selected", {
      typeahead_option_selected: suggestion.text,
      typeahead_option_rank: suggestion.index,
    });
  }

  bookingPageViewed(pageName: string) {
    return this.track("Page Viewed", {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.BookingPageModule,
      submodule: pageName,
    });
  }

  providerCategorySelected() {
    return this.track("Provider Selected");
  }

  providerListSearched(filterText: string) {
    return this.track("Provider List Searched", { filter_text: filterText });
  }

  searched() {
    return this.track("Appointment Inventory Searched");
  }

  searchFailed() {
    return this.track("Appointment Inventory Search Failed");
  }

  appointmentTypeSelected() {
    return this.track("Appointment Type Selected");
  }

  inventorySelected(inventory: AppointmentInventory) {
    const properties: any = {};
    if (!!inventory) {
      properties.appointment_inventory_id = inventory.id;
    }
    return this.track("Appointment Selected", properties);
  }

  filterApplied(filterType: string, filterDetails: object) {
    const properties = {
      filter_type: filterType,
      ...filterDetails,
    };
    return this.track("Filter Applied", properties);
  }

  paginateNext() {
    return this.track("Next Button Clicked");
  }

  paginatePrevious() {
    return this.track("Previous Button Clicked");
  }

  bookAppointmentClicked() {
    return this.track("Book Appointment Clicked");
  }

  bookingFailed() {
    return this.track("Appointment Booking Failed");
  }

  bookingCancelled() {
    return this.track("Appointment Booking Cancelled");
  }

  appointmentBooked(appointment: object) {
    return this.track("Appointment Booked", { appointment });
  }

  jumpAheadClicked() {
    return this.track("Jump Ahead Clicked");
  }

  addToCalendarClicked() {
    return this.track("Add To Calendar Clicked");
  }

  appointmentSurveySubmitted(surveyName: string) {
    return this.track("Survey Submitted", {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.BookingPageModule,
      submodule: surveyName,
    });
  }
  appointmentSurveyQuestionAnswered(surveyName: string, surveyQuestion: SurveyQuestion) {
    return this.track("Survey Question Answered", {
      flow: AppointmentAnalyticsProperty.BookingFlow,
      module: AppointmentAnalyticsProperty.BookingPageModule,
      submodule: surveyName,
      survey_question: surveyQuestion.questionText,
      survey_answer: surveyQuestion.patientAnswers.map(answer => answer.answerText).join(", "),
    });
  }
}
